import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { findPlace, onScreen as libOnScreen, PlaceGuide as LibPlaceGuide, type PlaceGuideData } from '@ui';
import { markJuiRoot } from '../jui/root';
import { getBases } from '../base';
import { BUG_OWN_ATTR } from '../recorder';
import { injectStyles } from '../styles';

/**
 * Report 35 (CRM, release_notes Рн8): a click on a note's place opens the screen AND shows the place itself — the element
 * is scrolled to, outlined with a pulse, and a callout next to it says what changed and how to use it. Not found — the
 * callout sits at the top of the screen and says where to look. The guide survives a full page load (sessionStorage):
 * a host without in-app navigation still gets it.
 */
export type PlaceGuide = PlaceGuideData;

const KEY = 'jaiora-bugs:place-guide';
/** A saved guide older than this is a leftover, not the click that just happened. */
const FRESH_MS = 30_000;
/** How long the screen may take to render the element after the navigation (tests shorten it). */
export const guideTiming = { waitMs: 5_000 };
const POLL_MS = 120;

type Saved = PlaceGuide & { at: number };

function save(guide: PlaceGuide): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...guide, at: Date.now() }));
  } catch {
    // Storage blocked: the in-app navigation path still works from memory.
  }
}

function take(): Saved | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as Saved;
    return saved && typeof saved.href === 'string' && typeof saved.at === 'number' ? saved : null;
  } catch {
    return null;
  }
}

function drop(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // nothing to drop
  }
}

/** The page is the guide's screen: the same path (with or without the host base path in front) and its query is there. */
export function onScreen(href: string, at: Pick<Location, 'origin' | 'pathname' | 'search'> = window.location, base = getBases().host): boolean {
  return libOnScreen(href, at, base);
}

const own = (el: Element) => !!el.closest(`[${BUG_OWN_ATTR}]`);

let shown: { close: () => void } | null = null;
let polling: number | null = null;

/** Takes the guide off the screen (button, Escape, a new guide) and stops waiting for one. */
export function closeGuide(): void {
  if (polling !== null) window.clearInterval(polling);
  polling = null;
  shown?.close();
  shown = null;
}

/**
 * The library's guide (`PlaceGuide`: ring over the element or none, the callout beside it or at the top) in the widget's
 * own node: `.jaiora-bugs` for the scoped sheet and theme, `data-bug-own` so the recorder and the next search skip it.
 */
function show(guide: PlaceGuide, target: HTMLElement | null): void {
  closeGuide();
  injectStyles();
  const box = document.createElement('div');
  box.className = 'jaiora-bugs bug-guide';
  box.setAttribute(BUG_OWN_ATTR, '');
  document.body.appendChild(box);
  const unmark = markJuiRoot(box);
  const root = createRoot(box);
  flushSync(() => root.render(createElement(LibPlaceGuide, { guide, target, onClose: closeGuide })));
  shown = {
    close: () => {
      box.remove();
      unmark();
      // Not inside the guide's own click handler: React unmounts after it.
      queueMicrotask(() => root.unmount());
    },
  };
}

/**
 * After a navigation (in-app or a page load): waits for the guide's screen and its element, then shows them; the
 * element never shows up — the callout at the top. A guide for another screen (the person went elsewhere) is dropped.
 */
export function resumeGuide(): void {
  if (typeof window === 'undefined') return;
  const saved = take();
  if (!saved) return;
  if (Date.now() - saved.at > FRESH_MS) {
    drop();
    return;
  }
  closeGuide();
  const started = Date.now();
  const tick = () => {
    const here = onScreen(saved.href);
    const target = here ? findPlace(saved.place, own) : null;
    const late = Date.now() - started >= guideTiming.waitMs;
    if (!target && !late) return;
    window.clearInterval(polling!);
    polling = null;
    drop();
    if (target) show(saved, target);
    else if (here) show(saved, null);
  };
  polling = window.setInterval(tick, POLL_MS);
  tick();
}

/** A click on a note's place: remember what to show, then the host navigates (or the browser loads the page). */
export function guideTo(guide: PlaceGuide): void {
  closeGuide();
  save(guide);
}
